import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { stadiumApi } from '../../services/footballApi';

interface Stadium {
  id: number;
  name: string;
  city?: string;
  country?: string;
  capacity?: number;
  is_international?: boolean;
  created_at?: string;
  updated_at?: string;
}

const FootballStadiums: React.FC = () => {
  const [stadiums, setStadiums] = useState<Stadium[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchStadiums = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await stadiumApi.getAll();
      setStadiums(res.data);
    } catch (err: any) {
      setError('Failed to fetch stadiums');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStadiums();
  }, [fetchStadiums]);

  const handleEdit = (id: number) => {
    navigate(`/football/stadiums/${id}/edit`);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this stadium?')) return;
    try {
      await stadiumApi.delete(id);
      fetchStadiums();
    } catch (err) {
      alert('Failed to delete stadium');
    }
  };

  const handleCreate = () => {
    navigate('/football/stadiums/new');
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
    { field: 'city', headerName: 'City', width: 140 },
    { field: 'country', headerName: 'Country', width: 140 },
    { field: 'capacity', headerName: 'Capacity', width: 120, type: 'number' },
    { field: 'is_international', headerName: 'International', width: 120, type: 'boolean' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: { row: Stadium }) => (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => handleEdit(params.row.id)}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Football Stadiums</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Stadium
        </Button>
      </Stack>
      <DataGrid
        rows={stadiums}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        getRowId={(row) => row.id}
        sx={{ backgroundColor: 'background.paper' }}
      />
      {error && <Typography color="error">{error}</Typography>}
    </Box>
  );
};

export default FootballStadiums; 